/**
 * FRONT-752: the wording the Memory entity panel's Research menu sends on the
 * user's behalf.
 *
 * The depth is carried by the WORDS, not by a side channel: the orchestrator
 * routes a research ask by reading the message (`src/agents/orchestration-prompt.ts`
 * — "deep research" / "deep dive" is the explicit deep trigger, everything else
 * is standard research run inline in the chat). So the deep variant must
 * literally open with "Deep research", and the standard variant's own wording
 * must not. (An entity whose NAME happens to contain "deep research" would tip
 * the standard ask over too — left alone deliberately: the worst case is a
 * more thorough answer than asked for, and stripping words out of the user's
 * own entity name would be a worse trade.)
 *
 * Pure and separate from the panel so both branches are unit-testable without
 * mounting React.
 */

export type ResearchDepth = 'standard' | 'deep';

/**
 * The ask names BOTH strands, in order, because the system-level rule alone
 * did not hold. Told only to "research X", the orchestrator read a briefing
 * saved twelve minutes earlier, treated it as current and never went to the
 * web — with a rule in the system prompt stating in as many words that a
 * stored document is never a reason to skip the web, and after opening its own
 * answer with "then I'll head to the web for what's changed" (observed live,
 * 2026-08-13, on a server confirmed to be running that rule). An instruction
 * in the user's own turn outweighs one buried in the system prompt, and this
 * ask IS the button's contract: pressing Research means go and look, not
 * re-read the filing cabinet.
 */
const ASK_TAIL =
  'Cover both sources. First what we already have on this — my documents and what the entry already knows. Then search the live web for what has changed since and anything the stored material misses. Do the web search even if a recent briefing already exists, and say so plainly if it turns up nothing new. Finish with the facts and connections worth adding to the knowledge graph.';

/**
 * Build the first message for a research chat launched from a memory entry.
 *
 * `name` is quoted so an entity whose name is a sentence still reads as one
 * subject, and trimmed so a stray newline can't split the message.
 */
export function buildEntityResearchAsk(name: string, depth: ResearchDepth): string {
  const subject = name.replace(/\s+/g, ' ').trim() || 'this entry';
  // The deep ask deliberately says nothing else about what it wants back: the
  // orchestrator lists "give me a sourced briefing on Z" as an example of the
  // INLINE research path, so adding that phrase here would put one trigger
  // from each rule into the same sentence.
  return depth === 'deep'
    ? `Deep research on "${subject}". ${ASK_TAIL}`
    : `Research "${subject}". ${ASK_TAIL}`;
}
